window.addEventListener('DOMContentLoaded', async () => {
	const canvas = document.getElementById('webgl-canvas');
	if (!canvas) return;

	await document.fonts.ready;

	// キャッチコピー用 DOM の取得
	const catchCopyItems = document.querySelectorAll('.catch-copy__item');

	// 1. シーン・カメラ・レンダラー
	const scene = new THREE.Scene();
	const camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.1, 1000);
	camera.position.z = 30;

	const renderer = new THREE.WebGLRenderer({
		canvas: canvas,
		antialias: true
	});
	renderer.setSize(window.innerWidth, window.innerHeight);
	renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

	// --- テクスチャローダーの準備 ---
	const textureLoader = new THREE.TextureLoader();

	// 2. 背景画像スライドショーの読み込み
	const bgImagePaths = [
		'/assets/images/bgi_unique-material_01.webp',
		'/assets/images/bgi_unique-material_02.webp',
		'/assets/images/bgi_unique-material_03.webp',
		'/assets/images/bgi_unique-material_04.webp'
	];

	const bgTextures = await Promise.all(
		bgImagePaths.map(path => new Promise(resolve => textureLoader.load(path, resolve)))
	);

	// 画像のアスペクト比規定サイズ (2880 x 1920)
	const imageResolution = new THREE.Vector2(2880, 1920);

	// スライドごとのオフセット設定（スマホ時に右にずらす場合、Xにマイナス値を設定）
	// 例: 1枚目 (index:0) のみスマホ時に右へ大きくずらす (-0.15)
	const getSlideOffsets = () => {
		const isMobile = window.innerWidth < 768;
		return [
			new THREE.Vector2(isMobile ? -0.1 : 0.0, 0.0), // 1枚目 (SP時のみ右にずらし)
			new THREE.Vector2(0.0, 0.0),                   // 2枚目
			new THREE.Vector2(0.0, 0.0),                   // 3枚目
			new THREE.Vector2(0.0, 0.0)                    // 4枚目
		];
	};

	let currentOffsets = getSlideOffsets();

	// 背景用マテリアル (object-fit: cover + offset 相当)
	const bgGeometry = new THREE.PlaneGeometry(2, 2);
	const bgMaterial = new THREE.ShaderMaterial({
		uniforms: {
			uTexCurrent: { value: bgTextures[0] },
			uTexNext: { value: bgTextures[1] },
			uOffsetCurrent: { value: currentOffsets[0].clone() },
			uOffsetNext: { value: currentOffsets[1].clone() },
			uMixRatio: { value: 0.0 },
			uResolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
			uImageResolution: { value: imageResolution }
		},
		vertexShader: `
			varying vec2 vUv;
			void main() {
				vUv = uv;
				gl_Position = vec4(position, 1.0);
			}
		`,
		fragmentShader: `
			uniform sampler2D uTexCurrent;
			uniform sampler2D uTexNext;
			uniform vec2 uOffsetCurrent;
			uniform vec2 uOffsetNext;
			uniform float uMixRatio;
			uniform vec2 uResolution;
			uniform vec2 uImageResolution;
			varying vec2 vUv;

			vec2 getCoverUv(vec2 uv, vec2 screenRes, vec2 imageRes, vec2 offset) {
				float screenAspect = screenRes.x / screenRes.y;
				float imageAspect = imageRes.x / imageRes.y;
				vec2 newUv = uv;

				if (screenAspect > imageAspect) {
					float scale = imageAspect / screenAspect;
					newUv.y = (uv.y - 0.5) * scale + 0.5;
				} else {
					float scale = screenAspect / imageAspect;
					newUv.x = (uv.x - 0.5) * scale + 0.5;
				}
				return newUv + offset;
			}

			void main() {
				vec2 coverUvCurrent = getCoverUv(vUv, uResolution, uImageResolution, uOffsetCurrent);
				vec2 coverUvNext = getCoverUv(vUv, uResolution, uImageResolution, uOffsetNext);

				vec4 c1 = texture2D(uTexCurrent, coverUvCurrent);
				vec4 c2 = texture2D(uTexNext, coverUvNext);
				
				gl_FragColor = mix(c1, c2, uMixRatio);
			}
		`,
		depthWrite: false,
		depthTest: false
	});

	const bgMesh = new THREE.Mesh(bgGeometry, bgMaterial);
	bgMesh.renderOrder = -1;
	scene.add(bgMesh);

	// 3. 人物画像スライドショーの読み込み
	const personImagePaths = [
		'/assets/images/img_unique-material_01.webp',
		'/assets/images/img_unique-material_02.webp',
		'/assets/images/img_unique-material_03.webp',
		'/assets/images/img_unique-material_04.webp'
	];

	const personEnergyColors = [
		{ c1: new THREE.Color('#ff4d1a'), c2: new THREE.Color('#e6198c') },
		{ c1: new THREE.Color('#00e5ff'), c2: new THREE.Color('#2979ff') },
		{ c1: new THREE.Color('#26b871'), c2: new THREE.Color('#cac800') },
		{ c1: new THREE.Color('#ffea00'), c2: new THREE.Color('#aa35d6') }
	];

	const personTextures = await Promise.all(
		personImagePaths.map(path => new Promise(resolve => textureLoader.load(path, resolve)))
	);

	// 人物マテリアル
	const personMat = new THREE.ShaderMaterial({
		uniforms: {
			uTexCurrent: { value: personTextures[0] },
			uTexNext: { value: personTextures[1] },
			uOffsetCurrent: { value: currentOffsets[0].clone() },
			uOffsetNext: { value: currentOffsets[1].clone() },
			uMixRatio: { value: 0.0 },
			uResolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
			uImageResolution: { value: imageResolution }
		},
		vertexShader: `
			varying vec2 vUv;
			void main() {
				vUv = uv;
				gl_Position = vec4(position, 1.0);
			}
		`,
		fragmentShader: `
			uniform sampler2D uTexCurrent;
			uniform sampler2D uTexNext;
			uniform vec2 uOffsetCurrent;
			uniform vec2 uOffsetNext;
			uniform float uMixRatio;
			uniform vec2 uResolution;
			uniform vec2 uImageResolution;
			varying vec2 vUv;

			vec2 getCoverUv(vec2 uv, vec2 screenRes, vec2 imageRes, vec2 offset) {
				float screenAspect = screenRes.x / screenRes.y;
				float imageAspect = imageRes.x / imageRes.y;
				vec2 newUv = uv;

				if (screenAspect > imageAspect) {
					float scale = imageAspect / screenAspect;
					newUv.y = (uv.y - 0.5) * scale + 0.5;
				} else {
					float scale = screenAspect / imageAspect;
					newUv.x = (uv.x - 0.5) * scale + 0.5;
				}
				return newUv + offset;
			}

			void main() {
				vec2 coverUvCurrent = getCoverUv(vUv, uResolution, uImageResolution, uOffsetCurrent);
				vec2 coverUvNext = getCoverUv(vUv, uResolution, uImageResolution, uOffsetNext);

				vec4 c1 = texture2D(uTexCurrent, coverUvCurrent);
				vec4 c2 = texture2D(uTexNext, coverUvNext);
				vec4 finalColor = mix(c1, c2, uMixRatio);

				if (finalColor.a < 0.05) discard;

				gl_FragColor = finalColor;
			}
		`,
		transparent: true,
		depthWrite: false,
		depthTest: false
	});

	const personGeo = new THREE.PlaneGeometry(2, 2);
	const personMesh = new THREE.Mesh(personGeo, personMat);
	personMesh.renderOrder = 3;
	personMesh.visible = true;

	scene.add(personMesh);

	// 4. レムニスケートカーブ
	class InfinityCurve extends THREE.Curve {
		constructor(scale = 48) {
			super();
			this.scale = scale;
		}
		getPoint(t, target = new THREE.Vector3()) {
			const angle = t * Math.PI * 2;
			const denom = 1 + Math.sin(angle) * Math.sin(angle);
			
			const x = (this.scale * Math.cos(angle)) / denom;
			const y = (this.scale * Math.sin(angle) * Math.cos(angle)) / denom;
			const z = -Math.sin(angle * 2) * 12.0;

			return target.set(x, y, z);
		}
	}

	// 5. ジオメトリ作成
	const path = new InfinityCurve(48);
	const geometry = new THREE.TubeGeometry(path, 200, 4, 32, true);

	// 6. リボン用カスタムシェーダー
	const customMaterial = new THREE.ShaderMaterial({
		uniforms: {
			uTime: { value: 0 },
			uColor1: { value: personEnergyColors[0].c1.clone() },
			uColor2: { value: personEnergyColors[0].c2.clone() }
		},
		vertexShader: `
			varying vec2 vUv;
			varying vec3 vNormal;
			varying vec3 vViewPosition;

			void main() {
				vUv = uv;
				vNormal = normalize(normalMatrix * normal);
				vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
				vViewPosition = -mvPosition.xyz;
				gl_Position = projectionMatrix * mvPosition;
			}
		`,
		fragmentShader: `
			uniform float uTime;
			uniform vec3 uColor1;
			uniform vec3 uColor2;

			varying vec2 vUv;
			varying vec3 vNormal;
			varying vec3 vViewPosition;

			#define PI 3.14159265359

			void main() {
				float timeFactor = uTime * 1.2;

				float angle = vUv.x * PI * 4.0 - timeFactor;
				float t = sin(angle) * 0.5 + 0.5;
				vec3 baseColor = mix(uColor1, uColor2, t);

				vec3 viewDir = normalize(vViewPosition);
				float dotNV = dot(vNormal, viewDir);
				float edgeAlpha = pow(abs(dotNV), 6.4);

				float stripes = sin(vUv.y * 320.0 + timeFactor * 4.0) * 0.5 + 0.5;
				stripes = pow(stripes, 16.0);
				float stripesFlow = sin(vUv.x * PI * 20.0 - timeFactor * 4.0) * 0.5 + 0.5;

				float coreGlow = pow(abs(dotNV), 9.6);
				vec3 finalColor = mix(baseColor, vec3(1.0), coreGlow * 0.35);

				finalColor += baseColor * (stripes * stripesFlow - 0.2) * 0.4;

				float finalAlpha = edgeAlpha * (0.1 + stripes * 0.9);

				gl_FragColor = vec4(finalColor, finalAlpha);
			}
		`,
		transparent: true,
		depthWrite: false,
		depthTest: true,
		blending: THREE.NormalBlending,
		side: THREE.DoubleSide
	});

	const infinityMesh = new THREE.Mesh(geometry, customMaterial);
	infinityMesh.renderOrder = 2;
	
	const mainGroup = new THREE.Group();
	mainGroup.add(infinityMesh);
	scene.add(mainGroup);

	// 10. GSAPを使ったスライド切り替え処理
	let currentIndex = 0;
	let isTransitioning = false;

	function goToNextSlide() {
		if (isTransitioning) return;
		isTransitioning = true;

		const nextIndex = (currentIndex + 1) % bgTextures.length;

		// 人物画像の更新準備
		personMat.uniforms.uTexCurrent.value = personTextures[currentIndex];
		personMat.uniforms.uTexNext.value = personTextures[nextIndex];
		personMat.uniforms.uOffsetCurrent.value.copy(currentOffsets[currentIndex]);
		personMat.uniforms.uOffsetNext.value.copy(currentOffsets[nextIndex]);

		// 背景画像の更新準備
		bgMaterial.uniforms.uTexCurrent.value = bgTextures[currentIndex];
		bgMaterial.uniforms.uTexNext.value = bgTextures[nextIndex];
		bgMaterial.uniforms.uOffsetCurrent.value.copy(currentOffsets[currentIndex]);
		bgMaterial.uniforms.uOffsetNext.value.copy(currentOffsets[nextIndex]);

		const colorTarget1 = personEnergyColors[nextIndex].c1;
		const colorTarget2 = personEnergyColors[nextIndex].c2;

		const tl = gsap.timeline({
			onComplete: () => {
				// 人物画像状態のリセット
				personMat.uniforms.uTexCurrent.value = personTextures[nextIndex];
				personMat.uniforms.uOffsetCurrent.value.copy(currentOffsets[nextIndex]);
				personMat.uniforms.uMixRatio.value = 0.0;

				// 背景画像状態のリセット
				bgMaterial.uniforms.uTexCurrent.value = bgTextures[nextIndex];
				bgMaterial.uniforms.uOffsetCurrent.value.copy(currentOffsets[nextIndex]);
				bgMaterial.uniforms.uMixRatio.value = 0.0;

				currentIndex = nextIndex;
				isTransitioning = false;
			}
		});

		// キャッチコピーのクラス切り替え（クロスフェードの開始に合わせる）
		tl.add(() => {
			if (catchCopyItems[currentIndex]) {
				catchCopyItems[currentIndex].classList.remove('is-active');
			}
			if (catchCopyItems[nextIndex]) {
				catchCopyItems[nextIndex].classList.add('is-active');
			}
		}, 0);

		// 人物画像のクロスフェード
		tl.to(personMat.uniforms.uMixRatio, {
			value: 1.0,
			duration: 1.5,
			ease: 'power2.inOut'
		}, 0);

		// 背景画像のクロスフェード
		tl.to(bgMaterial.uniforms.uMixRatio, {
			value: 1.0,
			duration: 1.5,
			ease: 'power2.inOut'
		}, 0);

		// リボンのカラーチェンジ
		tl.to(customMaterial.uniforms.uColor1.value, {
			r: colorTarget1.r,
			g: colorTarget1.g,
			b: colorTarget1.b,
			duration: 1.5,
			ease: 'power2.inOut'
		}, 0);

		tl.to(customMaterial.uniforms.uColor2.value, {
			r: colorTarget2.r,
			g: colorTarget2.g,
			b: colorTarget2.b,
			duration: 1.5,
			ease: 'power2.inOut'
		}, 0);
	}

	setInterval(goToNextSlide, 6000);

	let baseRangeX = 15;
	let baseRangeY = 8;

	// 11. アニメーションループ（30fps制限の実装）
	const clock = new THREE.Clock();
	const targetFPS = 30;
	const interval = 1 / targetFPS;
	let deltaAccumulator = 0;

	function animate() {
		requestAnimationFrame(animate);

		const deltaTime = clock.getDelta();
		deltaAccumulator += deltaTime;

		if (deltaAccumulator >= interval) {
			deltaAccumulator = deltaAccumulator % interval;

			const elapsedTime = clock.getElapsedTime();

			customMaterial.uniforms.uTime.value = elapsedTime;

			mainGroup.position.x = Math.sin(elapsedTime * 0.4) * (baseRangeX * 0.4);
			mainGroup.position.y = Math.cos(elapsedTime * 0.3) * (baseRangeY * 0.3);

			mainGroup.rotation.x = Math.sin(elapsedTime * 0.5) * 0.3;
			mainGroup.rotation.y = Math.cos(elapsedTime * 0.3) * 0.4;
			mainGroup.rotation.z = Math.sin(elapsedTime * 0.2) * 0.1;

			renderer.render(scene, camera);
		}
	}

	// 12. レスポンシブ＆レイアウト調整
	function updateLayout() {
		const currentWidth = window.innerWidth;
		const currentHeight = window.innerHeight;
		const isMobile = currentWidth < 768;

		baseRangeX = isMobile ? 10 : 20;
		baseRangeY = isMobile ? 5 : 10;

		// オフセット値の更新
		currentOffsets = getSlideOffsets();
		if (!isTransitioning) {
			bgMaterial.uniforms.uOffsetCurrent.value.copy(currentOffsets[currentIndex]);
			personMat.uniforms.uOffsetCurrent.value.copy(currentOffsets[currentIndex]);
		}

		bgMaterial.uniforms.uResolution.value.set(currentWidth, currentHeight);
		personMat.uniforms.uResolution.value.set(currentWidth, currentHeight);

		camera.aspect = currentWidth / currentHeight;
		camera.updateProjectionMatrix();
		renderer.setSize(currentWidth, currentHeight);
	}

	window.addEventListener('resize', updateLayout);
	updateLayout();

	animate();
});